import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { z } from 'zod'
import { zodValidator, fallback } from '@tanstack/zod-adapter'
import { Button } from '@/components/ui/button'
import { agentQueries } from '@/lib/api/queries/agent'
import { userQueries } from '@/lib/api/queries/user'
import { AgentCard } from '@/components/agents/agent-card'
import { AgentCardsSkeleton } from '@/components/agents/agent-card-skeleton'
import { AgentFilters } from '@/components/agents/agent-filters'
import { AgentEmptyState } from '@/components/agents/agent-empty-state'
import { PaginationControls } from '@/components/agents/pagination-controls'
import { usePermissions } from '@/lib/hooks/use-permissions'

const agentSearchSchema = z.object({
  search: fallback(z.string(), '').default(''),
  category: fallback(z.string(), '').default(''),
  status: fallback(z.string(), '').default(''),
  brand_id: fallback(z.string(), '').default(''),
  page: fallback(z.number(), 1).default(1),
  page_size: fallback(z.number(), 20).default(20),
  sort_by: fallback(z.string(), 'updated_at').default('updated_at'),
  sort_order: fallback(z.string(), 'desc').default('desc'),
})

export type AgentSearch = z.infer<typeof agentSearchSchema>

export const Route = createFileRoute('/_authenticated/agents/')({
  validateSearch: zodValidator(agentSearchSchema),
  component: AgentsListPage,
})

function AgentsListPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { canCreateAgent } = usePermissions()

  // Fetch user for brand filter options (per D-07)
  const userQuery = useQuery(userQueries.me())
  const brands = userQuery.data?.brands ?? []

  // Build query params -- strip empty strings and _all sentinel
  const queryParams = {
    search: search.search || undefined,
    category: search.category && search.category !== '_all' ? search.category : undefined,
    status: search.status && search.status !== '_all' ? search.status : undefined,
    brand_id: search.brand_id && search.brand_id !== '_all' ? search.brand_id : undefined,
    page: search.page,
    page_size: search.page_size,
    sort_by: search.sort_by,
    sort_order: search.sort_order,
  }

  const agentsQuery = useQuery(agentQueries.list(queryParams))

  const setFilters = (updates: Record<string, string | number>) => {
    navigate({
      search: (prev: AgentSearch) => {
        const next = { ...prev }
        for (const [key, value] of Object.entries(updates)) {
          // Map _all sentinel back to empty string for URL cleanliness
          ;(next as Record<string, unknown>)[key] = value === '_all' ? '' : value
        }
        return next
      },
    })
  }

  const clearFilters = () => {
    navigate({
      search: {
        search: '',
        category: '',
        status: '',
        brand_id: '',
        page: 1,
        page_size: 20,
        sort_by: 'updated_at',
        sort_order: 'desc',
      },
    })
  }

  const agents = agentsQuery.data?.data ?? []
  const pagination = agentsQuery.data?.pagination
  const total = pagination?.total ?? 0
  const totalPages = pagination?.totalPages ?? 0
  const hasFilters = !!(search.search || search.category || search.status || search.brand_id)

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-4xl font-medium font-serif text-foreground">Agents</h1>
        {canCreateAgent && (
          <Link to="/agents/create">
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Create Agent
            </Button>
          </Link>
        )}
      </div>
      <p className="text-base text-muted-foreground mb-8">
        Create and manage your AI agents
      </p>

      {/* Filter Bar per D-05, D-06 */}
      <AgentFilters
        search={search.search}
        category={search.category}
        status={search.status}
        brand_id={search.brand_id}
        sort_by={search.sort_by}
        sort_order={search.sort_order}
        brands={brands}
        onFilterChange={setFilters}
      />

      {/* Results Count */}
      {!agentsQuery.isPending && agents.length > 0 && (
        <p className="text-sm text-muted-foreground mb-4">
          Showing {agents.length} of {total} agents
        </p>
      )}

      {/* Agent Grid or Loading/Empty State */}
      {agentsQuery.isPending ? (
        <AgentCardsSkeleton />
      ) : agents.length === 0 ? (
        <AgentEmptyState
          variant={hasFilters ? 'no-results' : 'no-agents'}
          onClearFilters={clearFilters}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {agents.map((agent) => (
            <AgentCard key={agent.id} agent={agent} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationControls
          page={search.page}
          totalPages={totalPages}
          onPageChange={(page) => setFilters({ page })}
        />
      )}
    </div>
  )
}
